import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Layers, Droplets, Wrench, Construction, Ruler, Building2, Shield, DollarSign, HardHat } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import PageFAQ from "@/components/PageFAQ";
import RelatedServices from "@/components/RelatedServices";
import usePageSEO from "@/hooks/usePageSEO";
import { BUSINESS_LOGO } from "@/data/businessLogo";
import heroImg from "@/assets/lexington-reclaimed-oak-floor-fir-ceiling-great-room.webp";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const installationCostFAQs = [
  {
    q: "What is the average cost to install hardwood floors in Michigan?",
    a: "Installation cost depends on the method, floor width, subfloor condition, specialty patterns and the services required. Call (248) 325-8853 for a $50 estimate visit.",
  },
  {
    q: "Is engineered hardwood cheaper to install than solid?",
    a: "Not necessarily. Engineered hardwood is not inherently cheaper than solid hardwood — the installation method is what drives cost. A floating installation, which engineered hardwood often allows, is less expensive than a nail-down or nail-glue assist method. If your engineered floor is 5 inches or wider a nail-glue assist may still be required, bringing the cost in line with solid hardwood installation. Choose your installation method first, then evaluate the cost.",
  },
  {
    q: "Do you charge for subfloor evaluation?",
    a: "No. All estimates include a subfloor evaluation at the $50 estimate visit. We assess flatness, fastening, and moisture conditions before quoting any installation. Call (248) 325-8853 or visit our showroom at 33475 Seven Mile Rd, Livonia MI 48152.",
  },
  {
    q: "How long does hardwood floor installation take?",
    a: "Project timelines vary significantly depending on the installer, crew size, and attention to detail. We do not rush our installations. Timeline is discussed and agreed upon during the estimate process based on your specific project scope and subfloor conditions.",
  },
  {
    q: "What makes 1 Wood Floors different from other installers?",
    a: "Edmond Malaski holds Four NWFA Certifications as an Installer, Sand and Finish technician, Inspector, and Sales Advisor — one of very few contractors in Michigan to hold all four certifications simultaneously. As a Licensed Michigan Residential Builder we manage every aspect of the project including appliance coordination and plumbing. We follow manufacturer installation specifications on every project and we have the forensic inspection credentials to know exactly what happens when those specs are ignored.",
  },
];

const sections = [
  {
    icon: Layers,
    number: "01",
    title: "The Installation Method Drives Everything",
    content: "The cost to install hardwood floors in Michigan is primarily determined by one thing: the installation method. Choosing the wrong method for your specific environment — especially over Michigan basements or concrete slabs — is the number one cause of catastrophic floor failure. Before anyone talks price, the right installer talks method.",
    extra: "There are three primary installation methods. Floating installation is typically the most budget-friendly — the floor clicks or glues at the joints and floats over an underlayment. It requires the same subfloor flatness standards as any other method but uses no mechanical fasteners or full-spread adhesive. Standard nail-down installation is the industry standard for planks under five inches wide. Expect a range of $3 to $6 per square foot depending on the company's overhead, equipment quality, and installer experience. Nail-glue assist installation is required for any plank five inches or wider. Individual planks are both nailed and adhered with a specialized adhesive to prevent cupping and movement. This method adds $2 to $4 per square foot in materials and labor on top of your base installation cost. If staying within a tighter budget, we often recommend planks four inches or narrower specifically to avoid these mandatory technical add-ons.",
  },
  {
    icon: Droplets,
    number: "02",
    title: "The Vapor Barrier and Moisture Variable",
    content: "Over unfinished basements, crawlspaces, or concrete slabs, we apply a roll-on moisture vapor barrier before the adhesive. This ensures the adhesive bonds correctly while blocking moisture migration from below. Michigan's climate creates real moisture pressure — seasonal humidity changes affect wood movement year-round.",
    extra: "On concrete installations there are additional options: floating floors, glue-down installations, and insulating sound barrier underlayments that also provide thermal value. Most contractors never discuss these options with homeowners. As a Licensed Michigan Residential Builder and professional holding Four NWFA Certifications, we design the installation system first — then we price it.",
  },
  {
    icon: Wrench,
    number: "03",
    title: "Subfloor Preparation: The Hidden Budget Item",
    content: (
      <>
        Either you are paying for preparation or your installer is skipping it. There is no middle ground. Preparation means fastening and securing the existing <Link to="/subfloor-leveling" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">subfloor</Link> to eliminate squeaks, grinding high spots, and using compounds to achieve manufacturer-required flatness tolerances.
      </>
    ),
    extra: "A luxury wide-plank floor installed over an unprepared subfloor will feel bouncy, develop gaps, and fail prematurely. Always factor in $1 to $2 per square foot as an additional preparation line item — not included in your base installation quote. Sometimes a subfloor is nearly perfect — especially in newer construction. Sometimes it is loose, settled, or coming off an existing floor covering removal. You will not know until we evaluate it, which is why we assess the subfloor before quoting any installation.",
  },
  {
    icon: Construction,
    number: "04",
    title: "Demolition and Safety: What Is Under Your Feet",
    content: "Removing old flooring is not just demolition — it is a health and safety process. Carpet removal generally runs $0.50 to $1.00 per square foot additional. Tile removal can range from $4 to $10 per square foot depending on type, adhesive, and whether abatement testing is required. Hardwood removal typically runs $1 to $4 per square foot.",
    extra: "In Michigan homes built before 1980 this becomes more complex. Hard-good sheet vinyl, 9x9 tiles, and the adhesives used to install them often contain asbestos. Many contractors assume only 9x9 tiles require testing — this is incorrect. Sheet vinyl and its adhesives must also be tested before disturbance. Additionally, tile removal generates silica dust — a serious respiratory hazard. We use active dust containment on every tile demo project to protect your family and our crew. Contractors who skip this step are not giving you a deal. They are passing a health risk on to you.",
  },
  {
    icon: Ruler,
    number: "05",
    title: "Wide Plank: The Most Popular and Most Misunderstood",
    content: "Most homeowners today are selecting wide-plank flooring — five inches and wider. This is also where the most installation errors occur. Many installers will quote less on wide plank because they believe it installs faster. What they are not accounting for is the increased wood movement, the manufacturer requirements for nail-glue assist, and the greater consequences of subfloor imperfection.",
    extra: "At 1 Wood Floors we install wide plank as either a full glue spread, a nail-glue assist, or a combination of both — always following manufacturer specifications. Skipping the required adhesive on a wide plank floor to save money is how a $15,000 floor becomes a warranty dispute.",
  },
  {
    icon: Building2,
    number: "06",
    title: "Full Service vs. Basic Installation",
    content: (
      <>
        As a <Link to="/installation" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">Licensed Michigan Residential Builder</Link>, 1 Wood Floors can handle every aspect of the project — not just the floor itself. This includes furniture moving, appliance disconnection and reconnection, coordination with plumbers for valve replacements, and all trim and molding work.
      </>
    ),
    extra: "Basic installation quotes assume an empty house with a perfect subfloor and nothing in the way. Full service projects combining installation, subfloor prep, demolition, and coordination typically run $8 to $12 per square foot all-in — still significantly less than replacement.",
  },
  {
    icon: Shield,
    number: "07",
    title: "Flooring Removal and Site Protection",
    content: "Ask any installer you interview what steps they take to protect your home during demolition. Are dust barriers hung? Are they using HEPA filtration? What happens to the debris — is a dumpster provided or is removal included? These questions separate professional installers from crews that leave your home in worse shape than they found it.",
  },
];

const InstallationCost = () => {
  usePageSEO({
    title: "Hardwood Floor Installation Cost | 2026 Per Sq Ft Guide",
    description: "Real 2026 hardwood floor installation costs — solid, engineered, wide-plank. Subfloor prep, 5-Inch Rule, demo & disposal. Michigan pricing transparency.",
    path: "/installation-cost",
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ════════ HERO ════════ */}
        <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-end overflow-hidden">
          <img
            width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Great room with a reclaimed hit skip oak floor under a Douglas fir ceiling, Lexington, MI"
            className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/50 to-foreground/10" />
          <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-12 pb-12 md:pb-20 w-full">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <PageBreadcrumb
                items={[
                  { label: "Services", to: "/installation" },
                  { label: "Installation Cost" },
                ]}
              />
              <h1 className="font-display text-[clamp(28px,5.5vw,56px)] font-semibold text-primary-foreground leading-[1.05] mb-3 mt-4">
                Hardwood Floor Installation Cost in Michigan
              </h1>
              <p className="text-[14px] md:text-[17px] font-light text-primary-foreground/70 max-w-2xl leading-relaxed">
                What every Michigan homeowner should understand before hiring an installer — from an NWFA Certified Installer
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <a
                  href="tel:2483258853"
                  className="bg-primary-foreground text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-gold-400 transition-colors text-center inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-3.5 h-3.5" /> (248) 325-8853
                </a>
                <QuoteModal
                  trigger={
                    <button className="border border-primary-foreground/30 text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors text-center">
                      Book a $50 Estimate Visit
                    </button>
                  }
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ════════ CONTENT SECTIONS 1-7 ════════ */}
        {sections.map((section, i) => {
          const Icon = section.icon;
          const isAlt = i % 2 === 1;
          return (
            <section key={section.number} className={`${isAlt ? "bg-muted/30" : "bg-background"} py-16 md:py-24 border-t border-border`}>
              <div className="max-w-[900px] mx-auto px-5 md:px-12">
                <motion.div {...fadeUp}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary text-lg font-bold">
                      {section.number}
                    </span>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="font-display text-[clamp(22px,3.5vw,36px)] font-semibold text-foreground leading-[1.1] mb-6">
                    {section.title}
                  </h2>
                  <p className="text-[14px] md:text-[15px] font-normal text-muted-foreground leading-[1.8]">
                    {section.content}
                  </p>
                  {section.extra && (
                    <p className="text-[14px] md:text-[15px] font-normal text-muted-foreground leading-[1.8] mt-5">
                      {section.extra}
                    </p>
                  )}
                </motion.div>
              </div>
            </section>
          );
        })}

        {/* ════════ SECTION 8 — PRICING BREAKDOWN ════════ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[900px] mx-auto px-5 md:px-12">
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gold-400/20 text-gold-400 text-lg font-bold">
                  08
                </span>
                <DollarSign className="w-5 h-5 text-gold-400" />
              </div>
              <h2 className="font-display text-[clamp(22px,3.5vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-6">
                How Pricing Actually Works
              </h2>
              <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mb-8">
                Here is how the numbers build on a typical Michigan installation project. These are rough estimates — every floor is different and an in-home assessment is the only way to get an accurate number.
              </p>

              <div className="space-y-3">
                {[
                  { label: "Floating engineered hardwood, empty house, ready subfloor", price: "$3–6/sq ft" },
                  { label: "Standard nail-down solid or engineered hardwood, under 5\" width", price: "$3–6/sq ft" },
                  { label: "Subfloor preparation and fastening (when required)", price: "add $1–2/sq ft" },
                  { label: "Nail-glue assist method (5\" and wider), vapor barrier included", price: "add $2–4/sq ft" },
                  { label: "Full service — furniture moving, appliances, trim, plumbing coordination", price: "$8–12/sq ft" },
                  { label: "Custom species or specialty patterns (herringbone, diagonal)", price: "add $3–5/sq ft" },
                  { label: "Carpet removal", price: "add $0.50–1.00/sq ft" },
                  { label: "Tile removal (depends on type, adhesive, and abatement requirements)", price: "add $4–10/sq ft" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-4 border-b border-primary-foreground/10 pb-3">
                    <p className="text-[13px] md:text-[14px] font-normal text-primary-foreground/60 leading-relaxed">{item.label}</p>
                    <p className="text-[14px] md:text-[15px] font-semibold text-gold-400 whitespace-nowrap">{item.price}</p>
                  </div>
                ))}
              </div>

              <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mt-8">
                These are rough estimates only. Subfloor preparation, demolition, and coordination services are additional line items that vary by project. Your actual cost depends on your specific floor selection, installation method, subfloor condition, and the services required. Call{" "}
                <a href="tel:2483258853" className="text-gold-400 font-semibold hover:text-primary-foreground transition-colors">(248) 325-8853</a>{" "}
                for a $50 estimate visit.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ════════ FAQ ════════ */}
        <PageFAQ suppressJsonLd
          faqs={installationCostFAQs}
          heading={<>Installation cost questions <em className="italic font-normal text-primary">answered.</em></>}
          subheading="Michigan Installation Cost FAQs"
        />

        {/* ════════ FINAL CTA ════════ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 text-center">
            <motion.div {...fadeUp}>
              <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.08] mb-4">
                Ready for a forensic site evaluation and honest estimate
              </h2>
              <p className="text-[14px] md:text-[16px] font-normal text-primary-foreground/50 max-w-lg mx-auto leading-[1.7] mb-8">
                Call (248) 325-8853 or visit our Livonia showroom at 33475 Seven Mile Rd. $50 estimate visit. No pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:2483258853"
                  className="bg-primary-foreground text-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-gold-400 hover:text-foreground transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-3.5 h-3.5" /> Call (248) 325-8853
                </a>
                <QuoteModal
                  trigger={
                    <button className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors">
                      Schedule a Consultation
                    </button>
                  }
                />
              </div>
            </motion.div>
          </div>
        </section>

        <RelatedServices
          heading="More from our installation expertise."
          subheading="Related Services"
          links={[
            { title: "Hardwood Installation", desc: "See our full installation service page with method details, product specification, and process overview.", to: "/installation", tag: "Service" },
            { title: "Subfloor Leveling & Repair", desc: "The foundation under your floor determines its longevity. We engineer subfloors to NWFA tolerances.", to: "/subfloor-leveling", tag: "Service" },
            { title: "Refinishing Cost Guide", desc: "Already have hardwood? See what refinishing costs in Michigan and what drives the price.", to: "/refinishing-cost", tag: "Guide" },
            { title: "NWFA Certified Inspections", desc: "Floor failing after installation by another contractor? Our forensic inspections identify the root cause.", to: "/inspections", tag: "Service" },
          ]}
        />
      </main>
      <SiteFooter />

      {/* Service + FAQPage JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Hardwood Floor Installation",
            provider: {
              "@type": "LocalBusiness",
              logo: BUSINESS_LOGO,
              name: "1 Wood Floors",
              telephone: "+12483258853",
              address: {
                "@type": "PostalAddress",
                streetAddress: "33475 Seven Mile Rd",
                addressLocality: "Livonia",
                addressRegion: "MI",
                postalCode: "48152",
                addressCountry: "US",
              },
            },
            areaServed: { "@type": "State", name: "Michigan" },
            description: "Professional hardwood floor installation in Michigan by an NWFA Certified Installer. Nail-down, glue-assist, floating, and full-service coordination with forensic subfloor preparation.",
          }),
        }}
      />
    </div>
  );
};

export default InstallationCost;
